/**
 * Portal v2 layout shell.
 *
 * Activates Forest token scope via `data-ui="v2"` on root container; partner
 * branding (BrandingProvider) overrides --color-primary / --color-accent on
 * <html> so v2 primitives inherit the partner palette.
 *
 * Layout:
 *   - Desktop: persistent left sidebar (nav-only) + topbar (partner logo on
 *     left, user menu on right).
 *   - Mobile: topbar with menu trigger that opens the sidebar in a drawer
 *     (Sheet — Radix Dialog focus trap built in).
 *
 * No command palette, no feedback dialog — those belong on the partner-side
 * dashboard, not the customer portal.
 */
import {
  createFileRoute,
  Outlet,
  Link,
  useLocation,
  redirect,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useBranding } from "../lib/branding-context";
import { PortalBrand } from "@/components/portal-brand";
import { portalApi } from "../lib/portal-api";
import {
  PortalScopeProvider,
  usePortalScope,
} from "../lib/portal-scope-context";
import {
  LayoutDashboard,
  Globe,
  ShieldCheck,
  FileText,
  Users,
  Bell,
  Settings,
  LogOut,
  Menu,
  Check,
  ChevronsUpDown,
  ListChecks,
  ShieldAlert,
  ShieldQuestion,
} from "lucide-react";
import {
  Button,
  Avatar,
  AvatarFallback,
  Sheet,
  SheetContent,
  SheetTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ddmarc/ui";
import { getPortalCSRFToken } from "@/lib/portal-csrf";
import { useState } from "react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/portal")({
  beforeLoad: async () => {
    if (!getPortalCSRFToken()) {
      throw redirect({ to: "/login" });
    }
  },
  component: PortalLayoutWithScope,
});

function PortalLayoutWithScope() {
  return (
    <PortalScopeProvider>
      <PortalLayout />
    </PortalScopeProvider>
  );
}

const navGroups = [
  {
    label: "Overview",
    items: [
      { to: "/portal", icon: LayoutDashboard, label: "Dashboard", exact: true },
      { to: "/portal/action-center", icon: ListChecks, label: "Action center" },
      { to: "/portal/domains", icon: Globe, label: "Domains" },
    ],
  },
  {
    label: "Analysis",
    items: [
      { to: "/portal/reports", icon: FileText, label: "Reports" },
      { to: "/portal/senders", icon: Users, label: "Senders" },
      { to: "/portal/failures", icon: ShieldAlert, label: "Failures" },
      { to: "/portal/protection", icon: ShieldQuestion, label: "Protection" },
    ],
  },
  {
    label: "Manage",
    items: [
      { to: "/portal/policy", icon: ShieldCheck, label: "Policy" },
      { to: "/portal/alerts", icon: Bell, label: "Alerts" },
      { to: "/portal/settings", icon: Settings, label: "Settings" },
    ],
  },
];

type NavItem = (typeof navGroups)[number]["items"][number];

function PortalLayout() {
  const { branding } = useBranding();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const { data: user } = useQuery({
    queryKey: ["portal", "profile"],
    queryFn: portalApi.getProfile,
  });

  const { selectedDomainId } = usePortalScope();

  const { data: alerts } = useQuery({
    queryKey: ["portal", "alerts", "unread", selectedDomainId],
    queryFn: () =>
      portalApi.listAlerts({
        unread_only: true,
        size: 1,
        domain_id: selectedDomainId,
      }),
  });

  const unreadCount = alerts?.unread_count || 0;

  const handleLogout = async () => {
    await fetch(`/api/v1/portal/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "X-CSRF-Token": getPortalCSRFToken() ?? "" },
    });
    window.location.href = "/login";
  };

  const NavLink = ({ to, icon: Icon, label, exact }: NavItem) => {
    const isActive = exact
      ? location.pathname === to
      : location.pathname.startsWith(to);

    return (
      <Link
        to={to}
        aria-current={isActive ? "page" : undefined}
        className={cn(
          "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[12.5px] font-medium transition-colors",
          isActive
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-foreground"
        )}
        onClick={() => setMobileNavOpen(false)}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 truncate">{label}</span>
        {label === "Alerts" && unreadCount > 0 && (
          <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--v2-danger-soft)] px-1 text-[10px] font-semibold text-[var(--v2-danger)]">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Link>
    );
  };

  const SidebarNav = () => (
    <nav className="flex h-full flex-col gap-0.5 bg-sidebar p-3">
      {navGroups.map((group) => (
        <div key={group.label} className="flex flex-col gap-0.5">
          <div className="px-2.5 pb-1.5 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--v2-fg-subtle)]">
            {group.label}
          </div>
          {group.items.map((item) => (
            <NavLink key={item.to} {...item} />
          ))}
        </div>
      ))}
    </nav>
  );

  const initial =
    user?.name?.[0]?.toUpperCase() ||
    user?.email?.[0]?.toUpperCase() ||
    "U";

  const DomainScopeSwitcher = () => {
    const { domains, selectedDomainId, setSelectedDomainId, hasMultipleDomains } =
      usePortalScope();

    // A single-domain customer has nothing to switch between — showing a
    // control that always reads the same thing is noise, not information.
    if (!hasMultipleDomains) return null;

    const label =
      domains.find((d) => d.id === selectedDomainId)?.domain ?? "All domains";

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 max-w-[220px] gap-1.5 text-[12.5px]"
            aria-label={`Change domain scope, currently ${label}`}
          >
            <Globe className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{label}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 w-64 overflow-y-auto">
          <DropdownMenuLabel className="text-[11.5px] font-normal text-muted-foreground">
            Showing data for
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setSelectedDomainId(null)}
            className="flex items-center justify-between gap-2"
          >
            <span>All domains</span>
            {selectedDomainId === null && <Check className="h-3.5 w-3.5" />}
          </DropdownMenuItem>
          {domains.map((domain) => (
            <DropdownMenuItem
              key={domain.id}
              onClick={() => setSelectedDomainId(domain.id)}
              className="flex items-center justify-between gap-2"
            >
              <span className="truncate font-mono-data text-[12px]">
                {domain.domain}
              </span>
              {selectedDomainId === domain.id && (
                <Check className="h-3.5 w-3.5 shrink-0" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const UserMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-9 items-center gap-2 px-2"
        >
          <Avatar className="h-7 w-7">
            <AvatarFallback className="bg-[var(--color-sidebar-accent)] text-[12px] font-semibold text-foreground">
              {initial}
            </AvatarFallback>
          </Avatar>
          <span className="hidden text-[12.5px] font-medium text-foreground md:inline">
            {user?.name || user?.email}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-[12.5px] font-medium leading-none">
              {user?.name || user?.email}
            </p>
            {user?.customer_name && (
              <p className="text-[11.5px] leading-none text-muted-foreground">
                {user.customer_name}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/portal/settings" className="flex items-center gap-2">
            <Settings className="h-3.5 w-3.5" />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-[var(--v2-danger)]">
          <LogOut className="mr-2 h-3.5 w-3.5" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div data-ui="v2" className="flex min-h-screen bg-background">
      {/* Desktop sidebar — nav only, no logo or user menu */}
      <aside className="hidden w-56 shrink-0 border-r border-sidebar-border bg-sidebar md:block">
        <SidebarNav />
      </aside>

      {/* Right column: topbar + main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-border bg-card px-4">
          {/* Mobile menu trigger */}
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <SidebarNav />
            </SheetContent>
          </Sheet>

          {/* Partner logo */}
          <div className="flex items-center md:flex-none">
            <PortalBrand branding={branding} size="sm" />
          </div>

          {/* Domain scope — hidden for single-domain customers */}
          <div className="flex flex-1 items-center">
            <DomainScopeSwitcher />
          </div>

          {/* User menu */}
          <UserMenu />
        </header>

        <main className="flex-1">
          <div className="mx-auto w-full max-w-7xl p-4 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
